import { observer } from 'mobx-react';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import classNames from 'classnames';
import hash from 'object-hash';

import styles from './PresetFilters.module.scss';

import { usePresetFilters } from '@components/Table/filters/PresetFilters/hooks/usePresetFilters';
import { isDefined } from '@utils/isDefined';

import type { LocalSortState } from '../../VirtualTable/useLocalSort';

interface Props {
    selectedPreset?: number;
    presetFiltersParams: PresetFiltersParams;
    selectedFilters?: Record<any, any>;
}

export interface PresetFiltersParams {
    presetFilters: PresetFilter[];
    dataTest: string;
    presetByIndex?: boolean;
    omittedKeysForComparison?: string[];
    onPresetFilter(params: Record<any, any>, index?: number): Promise<void> | void;
}

export interface PresetFilter<T = Record<any, any>> {
    label: string;
    isDefaultPreset?: boolean;
    hint?: string;
    filters: T;
    sortingOrders?: Record<any, any>;
    /** Сортировка, которую таблица показывает и отправляет вместе с пресетом; без неё пресет сбрасывает сортировку. */
    sort?: LocalSortState;
}

export const PresetFilters = observer(
    ({
        selectedFilters,
        selectedPreset,
        presetFiltersParams: { dataTest, presetFilters, omittedKeysForComparison, onPresetFilter },
    }: Props) => {
        const { parseValueToArray } = usePresetFilters();

        return (
            <div className={styles.root}>
                {presetFilters.map((presetFilter, index) => {
                    const filters = parseValueToArray(presetFilter);

                    const isFiltersEqual = isDefined(omittedKeysForComparison)
                        ? isEqual(
                              omit(filters, omittedKeysForComparison),
                              omit(selectedFilters, omittedKeysForComparison),
                          )
                        : isEqual(filters, selectedFilters);

                    const isHighlighted =
                        (isDefined(selectedPreset) && index === selectedPreset) || (selectedFilters && isFiltersEqual);

                    return (
                        <div
                            data-test={`${dataTest}_${index}`}
                            data-testid={`${dataTest}_${index}`}
                            key={hash(presetFilter.filters)}
                            className={classNames([styles.button, isHighlighted && styles.highlighted])}
                            onClick={async () => {
                                await onPresetFilter(presetFilter.filters, index);
                            }}
                        >
                            {presetFilter.hint && <div className={styles.hint}>{presetFilter.hint}</div>}
                            {presetFilter.label}
                        </div>
                    );
                })}
            </div>
        );
    },
);
